import { combineLatest } from 'rxjs';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractListComponent } from '../../shared/abstract-list.component';
import { FilterSetService, FilterSet } from '../../../services/filter-set.service';
import { BbTableService } from '../../../services/bb-table.service';
import { ModalDefinition } from '../../../services/overlay.service';
import { Mediaclip } from '../../../interfaces/mediaclip';
import { WorklistService } from '../../../services/worklist.service';
import { LocalStorageService } from 'angular-2-local-storage';
import { TranscodingService, MediaclipJob } from '../../../services/transcoding.service';
import { UploadService } from '../../../services/upload.service';
import { Publication } from '../../../interfaces/publication';
import { PublicationService } from '../../../services/publication.service';

@Component({
	selector: 'vast-list',
	templateUrl: './vast-list.component.html',
	styleUrls: ['./vast-list.component.scss'],
	providers: [BbTableService, FilterSetService],
})

export class VastListComponent extends AbstractListComponent implements OnInit, OnDestroy {
	@ViewChild('previewTemplate') previewTemplate: TemplateRef<any>;

	playlistPanelOpen: boolean;
	putRight: boolean;
	deleteRight: boolean;
	hasDraftSelected: boolean;
	allowCustomPreset = false;
	baseFilterSet: FilterSet;

	rootPermission: boolean;
	previewClip: Mediaclip;
	disableImport = false;

	constructor(
		protected bbTableService: BbTableService,
		protected filterSetService: FilterSetService,
		protected localStorageService: LocalStorageService,
		protected worklistService: WorklistService,
		protected transcodingService: TranscodingService,
		protected uploadService: UploadService,
		protected publicationService: PublicationService) {

		super(bbTableService, filterSetService);

		this.listConfiguration = {
			name: 'creative',
			activePath: '/adservices/vastservice',
			basePath: '/adservices/vastservice/creatives/',
			entityTranslation: this.translate.instant('MEDIACLIP').toLowerCase(),
			entitiesTranslation: this.translate.instant('MEDIACLIPS').toLowerCase()
		};

		this.filterConfig = {
			search: true,
			filters: [
				{
					property: 'title',
					label: this.translate.instant('TITLE'),
					type: 'text'
				},
				{
					property: 'originalfilename',
					label: this.translate.instant('ORIGINAL_FILENAME'),
					type: 'text'
				}, {
					property: 'length_int', // can this be "length" ?
					label: this.translate.instant('DURATION'),
					type: 'facetquery',
					options: [
						{ label: '00 - 15s', query: '[0 TO 15]' },
						{ label: '15 - 30s', query: '[15 TO 30]' },
						{ label: '30 - 45s', query: '[30 TO 45]' },
						{ label: '45 - 60s', query: '[45 TO 60]' },
						{ label: '01 - 02m', query: '[60 TO 120]' },
						{ label: '02 - 03m', query: '[120 TO 180]' },
						{ label: '03 - 05m', query: '[180 TO 300]' },
						{ label: '05 - 10m', query: '[300 TO 600]' },
						{ label: '10 - 15m', query: '[600 TO 900]' },
						{ label: '15 - 30m', query: '[900 TO 1800]' },
						{ label: '30 - 45m', query: '[1800 TO 2700]' },
						{ label: '45 - 60m', query: '[2700 TO 3600]' },
						{ label: '01 - 02h', query: '[3600 TO 7200]' },
						{ label: '02h +', query: '[7200 TO *]' }
					]
				}, {
					property: 'deeplink',
					label: this.translate.instant('CLICK_THROUGH_URL'),
					type: 'text'
				}, {
					property: 'createdBy',
					label: this.translate.instant('CREATED_BY'),
					type: 'select'
				}, {
					property: 'updatedBy',
					label: this.translate.instant('UPDATED_BY'),
					type: 'select'
				}, {
					property: 'createddate',
					label: this.translate.instant('CREATED_DATE'),
					type: 'date'
				}, {
					property: 'updateddate',
					label: this.translate.instant('UPDATED_DATE'),
					type: 'date'
				}
			],
			presets: [
				{
					label: this.translate.instant('ALL'),
					value: 'all',
					filterSet: [],
				},
				{
					label: this.translate.instant('INTERACTIVE'),
					value: '[1 TO *]',
					filterSet: [{
						filters: [{
							type: 'commonclip',
							field: 'timelines',
							operator: 'is',
							value: '[1 TO *]'
						}]
					}],
				}]
		};
		this.getCustomPresets();

	}

	ngOnInit() {
		this.baseFilterSet = [{
			filters: [{
				type: 'commonclip',
				field: 'subusetype',
				operator: 'is',
				value: '"vast_creative"'
			}]
		},
		{
			filters: [{
				type: 'commonclip',
				field: 'mediatype',
				operator: 'is',
				value: '"video"'
			}]
		}];
		this.bbTableService
			.setBaseFilterSet(this.baseFilterSet)
			.setLimit(15)
			.sort('createddate', 'desc')
			.linkToUrl();

		this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight);
		this.authenticationService.hasPermission('delete').subscribe(hasRight => this.deleteRight = hasRight);

		this.authenticationService.hasPermission('root').subscribe(hasRight => this.rootPermission = hasRight);

		super.ngOnInit();

		this.subscriptions.add(combineLatest([this.bbTableService.rowsRetrievedObservable, this.transcodingService.jobsSubject])
			.subscribe((res: any[]) => {
				const mediaclipJobs: MediaclipJob[] = res[1];
				const mediaclipJobIdsArray = mediaclipJobs.map(mediaClipJob => mediaClipJob && mediaClipJob.id);
				for (let i = 0; i < this.rows.length; i++) {
					if (this.rows[i].data.mediatype === 'video') {
						if (mediaclipJobIdsArray.indexOf(this.rows[i].id) !== -1) {  // check if displayed mediaclips have jobs
							const foundMediaclipJob = mediaclipJobs.find(clipJob => clipJob && clipJob.id === this.rows[i].id);
							const average = this.transcodingService.getAverage(foundMediaclipJob.mediajobs);
							this.rows[i].transcodingAverage = average;
						}
					}
				}
			})
		);

		this.subscriptions.add(
			this.publicationService.getData(true).subscribe((publicationData: Publication) => {
				if (publicationData) {
					this.disableImport = publicationData.disableImport;
				}
			})
		);
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	onCreativeCreated() {
		setTimeout(() => {
			this.bbTableService.getRows(true);
		}, 2000);
	}

	showPreview(clip: Mediaclip) {
		this.previewClip = clip;
		const modal: ModalDefinition = {
			icon: 'film',
			title: clip.title || clip.originalfilename,
			component: {
				name: 'custom-content',
				templateRef: this.previewTemplate
			}
		};

		this.overlayService.createModal(modal);
	}
}
